import type { CallInfo } from '@compass/client/types/call';
import type { EligibilityCheck } from '@compass/client/types/eligibility';
import type { GetSummaryResponse } from '@compass/client/types/summary';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import {
  CALL_HEADERS,
  CASE_HEADERS,
  CSV_DELIMITER,
  DEFAULT_SHEET_NAME,
  DISPOSITION_HEADER,
  ELIGIBILITY_CHECK_HEADERS,
  EXCEL_MAX_CELL_LENGTH,
  SLA_HEADERS,
} from '@/constants/export';
import type {
  CallsExportOption,
  CaseExportData,
  ExportConfig,
  ExportHeader,
  ExportOptions,
  UIFieldInfo,
} from '@/types/export.types';
import type { SelectOption } from '@/types/formSchema';
import { formatCallType, getCallDuration, getCallStatusDisplay } from '@/utils/calls';
import { campaignCellValue } from '@/utils/campaignFields';
import { computeSlaValues } from '@/utils/sla';

import { isSystemDispositionCode } from './dispositions';

/**
 * Case headers for the export, renaming "Completed At" to "Submitted At" for
 * PQC workflows (where a case is submitted to the manufacturer, not completed).
 */
const resolveCaseHeaders = (isPqc: boolean): ExportHeader[] =>
  isPqc
    ? CASE_HEADERS.map(h =>
        h.name === 'workflow_completed_at' ? { ...h, label: 'Submitted At' } : h,
      )
    : CASE_HEADERS;

/**
 * Builds export headers for case records, calls, and eligibility checks
 */
export const buildExportHeaders = (
  inputFields: UIFieldInfo[],
  outputFields: UIFieldInfo[],
  outputGroups: string[],
  includeCalls: boolean = false,
  includeEligibilityChecks: boolean = false,
  includeDisposition: boolean = false,
  isPqc: boolean = false,
  includeSla: boolean = false,
): ExportHeader[] => {
  const headers: ExportHeader[] = [];

  headers.push(...resolveCaseHeaders(isPqc));

  // SLA headers (only for the payor workflow types) - kept adjacent to the
  // other case-level metadata.
  if (includeSla) {
    headers.push(...SLA_HEADERS);
  }

  // Disposition header (only for PBM/UNIFIEDMEDBV workflows)
  if (includeDisposition) {
    headers.push(DISPOSITION_HEADER);
  }

  // Call-specific headers (if including calls)
  if (includeCalls) {
    headers.push(...CALL_HEADERS);
  }

  // Eligibility check headers (if including eligibility checks)
  if (includeEligibilityChecks) {
    headers.push(...ELIGIBILITY_CHECK_HEADERS);
  }

  // Input field headers use the schema field id, matching the campaign export
  // (BI-1753) so a case download carries the same column names whether or not
  // calls / eligibility checks are included.
  inputFields.forEach(field => {
    headers.push({
      name: `input_${field.id}`,
      label: field.id,
      type: 'string',
    });
  });

  // Output field headers are id-based too, but keep the `output_` prefix: unlike
  // the campaign format (input columns only) this file carries input AND output
  // columns, and a schema can use the same field id on both sides.
  outputFields.forEach(field => {
    headers.push({
      name: `output_${field.id}`,
      label: `output_${field.id}`,
      type: 'string',
    });
  });

  // Section-summary columns, one per output group.
  outputGroups.forEach(groupName => {
    headers.push({
      name: `summary_${groupName}`,
      label: `summary_${groupName}`,
      type: 'string',
    });
  });

  return headers;
};

// Helper function to add case columns (for call/eligibility rows - mostly nulls)
const addCaseColumns = (
  row: (string | number | null)[],
  caseRecordId: string | undefined,
  isCallRow: boolean = false,
  includeDisposition: boolean = false,
  includeSla: boolean = false,
): void => {
  if (isCallRow) {
    row.push(caseRecordId || null, null, null, null, null, null, null);
    // SLA values belong to the case, not to its calls/eligibility checks, so
    // these rows only pad the columns to keep every row the same width.
    if (includeSla) {
      row.push(null, null);
    }
    if (includeDisposition) {
      row.push(null);
    }
  }
};

// Helper to extract call data values
/* eslint-disable complexity -- array construction with optional chaining has high cyclomatic complexity but is simple and readable */
const getCallDataArray = (call: CallInfo): (string | number | null)[] => [
  call.sid || null,
  call.input_data?.agent_persona || null,
  call.created_at || null,
  getCallDuration(call.started_at, call.ended_at),
  formatCallType(call.type),
  getCallStatusDisplay(call.status),
  call.disposition?.type || null,
  call.disposition?.notes || null,
  call.started_at || null,
  call.chart_info?.notes || null,
  call.phone || null,
];
/* eslint-enable complexity */

// Helper function to add call columns
const addCallColumns = (
  row: (string | number | null)[],
  call: CallInfo | null,
  includeCalls: boolean,
): void => {
  if (!includeCalls) return;

  if (call) {
    row.push(...getCallDataArray(call));
  } else {
    row.push(null, null, null, null, null, null, null, null, null, null, null);
  }
};

// Helper function to add eligibility check placeholder columns
const addEligibilityCheckPlaceholders = (
  row: (string | number | null)[],
  includeEligibilityChecks: boolean,
): void => {
  if (includeEligibilityChecks) {
    row.push(null, null, null, null, null, null, null, null);
  }
};

/**
 * Resolves a raw value to its human-readable label using field options.
 * Returns null for null/undefined values, or the label if found, otherwise the raw value as string.
 */
export const resolveValueToLabel = (value: any, options?: SelectOption[]): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (options && options.length > 0) {
    const option = options.find(opt => opt.value === value);
    if (option) {
      return option.label;
    }
  }
  return String(value);
};

// Helper function to add field data
const addFieldData = (
  row: (string | number | null)[],
  fields: UIFieldInfo[],
  data: Record<string, any> | undefined,
): void => {
  fields.forEach(field => {
    const value = data?.[field.id];

    // Leaves of nested array/object fields (`drug.code`) live behind a dotted
    // path, and repeated ones hold several values. Resolve those the way the
    // campaign format does: one cell, values joined with `|`. Without this a
    // container serialised as `[object Object]`.
    if (field.id.includes('.') || (value !== null && typeof value === 'object')) {
      row.push(campaignCellValue(data ?? {}, field.id) || null);
      return;
    }

    row.push(resolveValueToLabel(value, field.options));
  });
};

// Helper function to add summary placeholders
const addSummaryPlaceholders = (row: (string | number | null)[], outputGroups: string[]): void => {
  outputGroups.forEach(() => row.push(null));
};

/**
 * Converts a call record to an export row
 */
export const callToExportRow = (
  call: CallInfo,
  inputFields: UIFieldInfo[],
  outputFields: UIFieldInfo[],
  outputGroups: string[],
  includeCalls: boolean = false,
  includeEligibilityChecks: boolean = false,
  includeDisposition: boolean = false,
  includeSla: boolean = false,
): (string | number | null)[] => {
  const row: (string | number | null)[] = [];

  // Case-specific columns - empty for call rows
  addCaseColumns(row, call.caserecord_id, true, includeDisposition, includeSla);

  // Call-specific columns
  addCallColumns(row, call, includeCalls);

  // Eligibility check columns - empty for call rows
  addEligibilityCheckPlaceholders(row, includeEligibilityChecks);

  // Input and output data fields
  addFieldData(row, inputFields, call.input_data);
  addFieldData(row, outputFields, call.output_data);

  // Summary columns - empty for call rows
  addSummaryPlaceholders(row, outputGroups);

  return row;
};

// Helper function to format eligibility check errors
const formatEligibilityErrors = (errors: any[] | undefined): string | null => {
  if (!errors) return null;
  return errors
    .map(err => `${err.field || 'Unknown'}: ${err.description || 'No description'}`)
    .join('; ');
};

// Helper function to add eligibility check columns
const addEligibilityCheckColumns = (
  row: (string | number | null)[],
  eligibilityCheck: EligibilityCheck | null,
  includeEligibilityChecks: boolean,
): void => {
  if (!includeEligibilityChecks) return;

  if (eligibilityCheck) {
    const errorsStr = formatEligibilityErrors(eligibilityCheck.errors);
    row.push(
      eligibilityCheck.id || null,
      eligibilityCheck.status || null,
      eligibilityCheck.created_at || null,
      eligibilityCheck.updated_at || null,
      eligibilityCheck.payer_id || null,
      eligibilityCheck.attempt || null,
      errorsStr,
      eligibilityCheck.case_state || null,
    );
  } else {
    row.push(null, null, null, null, null, null, null, null);
  }
};

/**
 * Converts an eligibility check to an export row
 */
const eligibilityCheckToExportRow = (
  eligibilityCheck: EligibilityCheck,
  inputFields: UIFieldInfo[],
  outputFields: UIFieldInfo[],
  outputGroups: string[],
  includeCalls: boolean = false,
  includeEligibilityChecks: boolean = false,
  includeDisposition: boolean = false,
  includeSla: boolean = false,
): (string | number | null)[] => {
  const row: (string | number | null)[] = [];

  // Case-specific columns - empty for eligibility check rows
  addCaseColumns(row, eligibilityCheck.caserecord_id, true, includeDisposition, includeSla);

  // Call-specific columns - empty for eligibility check rows
  addCallColumns(row, null, includeCalls);

  // Eligibility check columns
  addEligibilityCheckColumns(row, eligibilityCheck, includeEligibilityChecks);

  // Input and output data fields
  addFieldData(row, inputFields, (eligibilityCheck as any).input_data);
  addFieldData(row, outputFields, (eligibilityCheck as any).output_data);

  // Summary columns - empty for eligibility check rows
  addSummaryPlaceholders(row, outputGroups);

  return row;
};

// Resolve created_by user ID to email
const resolveCreatedByEmail = (
  createdBy: string | undefined,
  userEmailMap?: Map<string, string>,
): string | null => {
  if (!createdBy) return null;
  return userEmailMap?.get(createdBy) ?? null;
};

// Resolve disposition code to human-readable label
const resolveDispositionLabel = (
  dispositionCode: number | undefined | null,
  dispositionsMap?: Map<number, string>,
): string | null => {
  if (dispositionCode == null) return null;
  // System codes (0, 101-106) are telephony outcomes stamped by case_sync, not benefit
  // outcomes anyone chose, and none appear in a workflow's catalog — so the fallback
  // below used to export the bare number ("104"). Export them as N/A, matching the
  // cases table.
  if (isSystemDispositionCode(dispositionCode)) return 'N/A';
  return dispositionsMap?.get(dispositionCode) ?? String(dispositionCode);
};

/** The seven fixed case columns, in CASE_HEADERS order. */
const resolveBaseCaseCells = (
  caseRecord: CaseExportData,
  userEmailMap?: Map<string, string>,
): (string | number | null)[] => [
  caseRecord.code || caseRecord.id || null,
  caseRecord.reference_id || null,
  caseRecord.state || null,
  caseRecord.assigned || null,
  resolveCreatedByEmail(caseRecord.created_by, userEmailMap),
  caseRecord.created_at || null,
  caseRecord.workflow_completed_at || null,
];

/**
 * Resolves the SLA Flag / SLA Timer cells for a case row (BV-1846).
 *
 * The SLA budget and the workflow version live on the *workflow*, so both are
 * looked up per row from the maps the export service supplies - mirroring how
 * TableBuilder resolves them for the case-list timer. The values themselves come
 * from the shared `computeSlaValues`, so a row reads exactly like its
 * table cell.
 */
const resolveSlaCells = (
  caseRecord: CaseExportData,
  slaByWorkflowId?: Map<string, number>,
  versionByWorkflowId?: Map<string, string>,
): { slaFlag: string; slaTimer: string } => {
  const workflowId = caseRecord.workflow_id;
  return computeSlaValues({
    workflowLastStartedAt: caseRecord.workflow_last_started_at,
    workflowDurationSeconds: caseRecord.workflow_duration_seconds,
    slaMinutes: workflowId ? slaByWorkflowId?.get(workflowId) : undefined,
    isBreached: caseRecord.status_flags?.sla_breach,
    // Completed cases carry workflow_completed_at; terminated/cancelled cases
    // carry workflow_cancelled_at. Mirror TableBuilder's case-list timer so a
    // terminated case reports its total turnaround instead of falling through
    // to the running-case branch that counts against `now`.
    completedAt: caseRecord.workflow_completed_at || caseRecord.workflow_cancelled_at,
    workflowState: caseRecord.workflow_state,
    slaRiskEscalated: caseRecord.status_flags?.sla_risk_escalated,
    workflowVersion: workflowId ? versionByWorkflowId?.get(workflowId) : undefined,
  });
};

// Helper function to add full case record columns
const addFullCaseColumns = (
  row: (string | number | null)[],
  caseRecord: CaseExportData,
  userEmailMap?: Map<string, string>,
  includeDisposition: boolean = false,
  dispositionsMap?: Map<number, string>,
  includeSla: boolean = false,
  slaByWorkflowId?: Map<string, number>,
  versionByWorkflowId?: Map<string, string>,
): void => {
  row.push(...resolveBaseCaseCells(caseRecord, userEmailMap));
  if (includeSla) {
    const { slaFlag, slaTimer } = resolveSlaCells(caseRecord, slaByWorkflowId, versionByWorkflowId);
    row.push(slaFlag, slaTimer);
  }
  if (includeDisposition) {
    row.push(resolveDispositionLabel(caseRecord.disposition_code, dispositionsMap));
  }
};

// Helper function to add summary columns
const addSummaryColumns = (
  row: (string | number | null)[],
  outputGroups: string[],
  summaryResponse?: GetSummaryResponse,
): void => {
  outputGroups.forEach(groupName => {
    const summaryText = summaryResponse?.summary?.content?.section_summaries?.[groupName];
    row.push(summaryText || null);
  });
};

/**
 * Converts a case record to an export row
 */
export const caseToExportRow = (
  caseRecord: CaseExportData,
  inputFields: UIFieldInfo[],
  outputFields: UIFieldInfo[],
  outputGroups: string[],
  summaryResponse?: GetSummaryResponse,
  includeCalls: boolean = false,
  includeEligibilityChecks: boolean = false,
  userEmailMap?: Map<string, string>,
  includeDisposition: boolean = false,
  dispositionsMap?: Map<number, string>,
  includeSla: boolean = false,
  slaByWorkflowId?: Map<string, number>,
  versionByWorkflowId?: Map<string, string>,
): (string | number | null)[] => {
  const row: (string | number | null)[] = [];

  // Case-specific columns
  addFullCaseColumns(
    row,
    caseRecord,
    userEmailMap,
    includeDisposition,
    dispositionsMap,
    includeSla,
    slaByWorkflowId,
    versionByWorkflowId,
  );

  // Call-specific columns - empty for case record rows
  addCallColumns(row, null, includeCalls);

  // Eligibility check columns - empty for case record rows
  addEligibilityCheckColumns(row, null, includeEligibilityChecks);

  // Input and output data fields
  const inputData = caseRecord.input_data?.data as Record<string, any> | undefined;
  const outputData = caseRecord.output_data?.data as Record<string, any> | undefined;
  addFieldData(row, inputFields, inputData);
  addFieldData(row, outputFields, outputData);

  // Summary columns
  addSummaryColumns(row, outputGroups, summaryResponse);

  return row;
};

/**
 * Prepares export configuration with interleaved rows
 */
export const prepareExportConfig = (
  caseRecords: CaseExportData[],
  inputFields: UIFieldInfo[],
  outputFields: UIFieldInfo[],
  outputGroups: string[],
  summariesMap: Map<string, GetSummaryResponse>,
  includeEligibilityChecks: boolean = false,
  callsOption: CallsExportOption,
  userEmailMap?: Map<string, string>,
  dispositionsMap?: Map<number, string>,
  isPqc: boolean = false,
  includeSla: boolean = false,
  slaByWorkflowId?: Map<string, number>,
  versionByWorkflowId?: Map<string, string>,
): ExportConfig => {
  const includeCalls = callsOption === 'include' || callsOption === 'only';
  // Only include disposition column when dispositions data is available
  // (i.e., for PBM/UNIFIEDMEDBV workflows that have disposition codes)
  const includeDisposition = !!dispositionsMap && dispositionsMap.size > 0;

  const headers = buildExportHeaders(
    inputFields,
    outputFields,
    outputGroups,
    includeCalls,
    includeEligibilityChecks,
    includeDisposition,
    isPqc,
    includeSla,
  );
  const rows: (string | number | null)[][] = [];

  // Process each case record
  caseRecords.forEach(caseRecord => {
    // First add call rows (if including calls and calls exist)
    if (includeCalls && caseRecord.calls) {
      caseRecord.calls.forEach(call => {
        rows.push(
          callToExportRow(
            call,
            inputFields,
            outputFields,
            outputGroups,
            includeCalls,
            includeEligibilityChecks,
            includeDisposition,
            includeSla,
          ),
        );
      });
    }

    // Then add eligibility check rows (if including eligibility checks and they exist)
    if (includeEligibilityChecks && caseRecord.eligibility_checks) {
      caseRecord.eligibility_checks.forEach(eligibilityCheck => {
        rows.push(
          eligibilityCheckToExportRow(
            eligibilityCheck,
            inputFields,
            outputFields,
            outputGroups,
            includeCalls,
            includeEligibilityChecks,
            includeDisposition,
            includeSla,
          ),
        );
      });
    }

    // Finally add the case record row
    if (callsOption !== 'only') {
      const summaryResponse = caseRecord.id ? summariesMap.get(caseRecord.id) : undefined;
      rows.push(
        caseToExportRow(
          caseRecord,
          inputFields,
          outputFields,
          outputGroups,
          summaryResponse,
          includeCalls,
          includeEligibilityChecks,
          userEmailMap,
          includeDisposition,
          dispositionsMap,
          includeSla,
          slaByWorkflowId,
          versionByWorkflowId,
        ),
      );
    }
  });

  return { headers, rows };
};

/**
 * Generates CSV file from export configuration
 */
export const generateCSV = (config: ExportConfig): string => {
  // Prepare data with header row
  const data = [config.headers.map(h => h.label), ...config.rows];

  // Convert to CSV using Papa Parse
  const csv = Papa.unparse(data, {
    quotes: true,
    delimiter: CSV_DELIMITER,
  });

  return csv;
};

/**
 * Truncates a cell value to Excel's maximum character limit
 * Excel cells have a hard limit of 32,767 characters
 */
const truncateCellValue = (
  value: string | number | null | undefined,
): string | number | null | undefined => {
  if (value === null || value === undefined || typeof value === 'number') {
    return value;
  }
  if (value.length > EXCEL_MAX_CELL_LENGTH) {
    return `${value.substring(0, EXCEL_MAX_CELL_LENGTH - 3)}...`;
  }
  return value;
};

/**
 * Generates XLSX file from export configuration
 */
export const generateXLSX = (
  config: ExportConfig,
  sheetName: string = DEFAULT_SHEET_NAME,
): ArrayBuffer => {
  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Prepare data with header row, truncating cell values to avoid Excel errors
  const truncatedRows = config.rows.map(row => row.map(truncateCellValue));
  const data = [config.headers.map(h => h.label), ...truncatedRows];

  // Convert to worksheet
  const ws = XLSX.utils.aoa_to_sheet(data);

  // Auto-fit columns (estimate width based on content)
  const colWidths: { wch: number }[] = [];
  config.headers.forEach((header, index) => {
    let maxWidth = header.label.length;

    // Check data rows for max width
    config.rows.forEach(row => {
      const cellValue = row[index];
      if (cellValue !== null && cellValue !== undefined) {
        const cellLength = String(cellValue).length;
        if (cellLength > maxWidth) {
          maxWidth = cellLength;
        }
      }
    });

    // Cap at reasonable max width and add padding
    colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
  });

  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Write to array buffer
  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

  return arrayBuffer;
};

/**
 * Sanitizes a string for use as a filename segment: collapses runs of anything
 * outside [a-z0-9_-] to '-', trims leading/trailing dashes, and caps the length.
 * Falls back to 'export' when nothing usable survives.
 */
export const sanitizeFilenamePart = (value: string | undefined | null): string =>
  value
    ?.trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'export';

/**
 * Downloads a file from data
 */
export const downloadFile = (
  data: string | ArrayBuffer,
  filename: string,
  mimeType: string,
): void => {
  // Create blob from data
  const blob = new Blob([data], { type: mimeType });

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Main export function that handles both CSV and XLSX formats
 */
export const exportData = (config: ExportConfig, options: ExportOptions): void => {
  // Generate filename if not provided
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const defaultFilename = `cases_${timestamp}`;
  const filename = options.filename || defaultFilename;

  if (options.format === 'csv') {
    // Generate CSV
    const csv = generateCSV(config);
    downloadFile(csv, `${filename}.csv`, 'text/csv');
  } else {
    // Generate XLSX
    const xlsx = generateXLSX(config, options.sheetName);
    downloadFile(
      xlsx,
      `${filename}.xlsx`,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  }
};
